import React, { Suspense } from 'react';
import useBackendHealth from '../hooks/useBackendHealth';
import useNetworkQuality from '../hooks/useNetworkQuality';

const isChunkLoadError = (error) => {
    const message = String(error?.message || error || '').toLowerCase();
    return message.includes('chunkloaderror') || message.includes('loading chunk') || message.includes('failed to fetch dynamically imported module');
};

class RouteChunkBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, isChunkError: false };
    }

    static getDerivedStateFromError(error) {
        return {
            hasError: true,
            isChunkError: isChunkLoadError(error)
        };
    }

    componentDidCatch(error) {
        if (isChunkLoadError(error) && typeof window !== 'undefined') {
            const reloadKey = 'loadiq_chunk_reload_attempted';
            const alreadyReloaded = sessionStorage.getItem(reloadKey) === '1';

            if (!alreadyReloaded) {
                sessionStorage.setItem(reloadKey, '1');
                setTimeout(() => window.location.reload(), 100);
            }
        }
    }

    handleRetry = () => {
        if (typeof window !== 'undefined') {
            sessionStorage.removeItem('loadiq_chunk_reload_attempted');
            window.location.reload();
        }
    };

    render() {
        const { hasError, isChunkError } = this.state;

        if (hasError) {
            if (isChunkError) {
                return (
                    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6 text-white">
                        <div className="max-w-md w-full rounded-3xl border border-white/10 bg-slate-900/90 p-6 sm:p-8 text-center shadow-2xl">
                            <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-indigo-500/20 flex items-center justify-center">
                                <div className="h-6 w-6 rounded-full border-2 border-indigo-300 border-t-transparent animate-spin" />
                            </div>
                            <h1 className="text-xl sm:text-2xl font-black text-white">Refreshing app assets</h1>
                            <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                                A page chunk could not load, so we&apos;re trying to refresh the app with the latest files.
                                If the app stays stuck, reload once more.
                            </p>
                            <button
                                onClick={this.handleRetry}
                                className="mt-6 inline-flex items-center justify-center rounded-xl bg-indigo-600 px-4 py-3 text-xs font-black uppercase tracking-widest text-white transition-colors hover:bg-indigo-500"
                            >
                                Reload App
                            </button>
                        </div>
                    </div>
                );
            }
        }

        return this.props.children;
    }
}

const MinimalRouteLoader = () => (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 text-white">
        <div className="h-12 w-12 rounded-full border-4 border-white/10 border-t-indigo-500 animate-spin" />
    </div>
);

const RouteSkeletonGate = ({ component: Component, skeleton: Skeleton, requiresBackend = false, skeletonProps = {} }) => {
    const network = useNetworkQuality();
    const backendHealth = useBackendHealth(requiresBackend);
    const shouldWaitForBackend = requiresBackend && (backendHealth.isChecking || !backendHealth.isOnline);
    const suspenseFallback = network.isLowBandwidth
        ? <Skeleton {...skeletonProps} />
        : <MinimalRouteLoader />;

    if (shouldWaitForBackend) {
        return <Skeleton {...skeletonProps} backendMode />;
    }

    return (
        <RouteChunkBoundary>
            <Suspense fallback={suspenseFallback}>
                <Component />
            </Suspense>
        </RouteChunkBoundary>
    );
};

export default RouteSkeletonGate;
